import React from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
// Import Local Components
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import Register from './components/Register';
import ResetPassword from './components/ResetPassword';
import MFASetup from './components/MFASetup';
import MFAVerify from './components/MFAVerify';
// Import Layouts
import BaseLayout from '../../layouts/Base';
import RouteWrapper from './components/Route';

function Auth({
  email,
  password,
  updateUser,
  updateLoading,
  updateEmail,
  onLoginSubmit,
  handleLoginKeyUp,
  forgotPassword,
  formError,
  emailSent,
  error,
  loading,
  user,
  onUpdatePassword,
  onUpdateEmail,
  updateFormError,
  updateError,
  passwordConfirm,
  onUpdatePasswordConfirm,
  alert,
  updateEmailSent,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <BaseLayout>
      <Routes>
        <Route
          path="/login"
          element={
            <RouteWrapper title="Login">
              <Login
                formError={formError}
                email={email}
                password={password}
                onUpdateEmail={onUpdateEmail}
                onUpdatePassword={onUpdatePassword}
                loading={loading}
                onLoginSubmit={onLoginSubmit}
                handleLoginKeyUp={handleLoginKeyUp}
                forgotPassword={() => {
                  updateFormError(null);
                  navigate('/auth/forgot-password');
                }}
              />
            </RouteWrapper>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <RouteWrapper title="Password Reset">
              <ForgotPassword
                formError={formError}
                email={email}
                updateEmail={updateEmail}
                forgotPassword={forgotPassword}
                emailSent={emailSent}
                goBack={() => {
                  updateFormError(null);
                  updateEmailSent(false);
                  navigate('/auth/login');
                }}
                alert={alert}
              />
            </RouteWrapper>
          }
        />
        <Route
          path="/register"
          element={
            <RouteWrapper title="Register">
              <Register
                error={error}
                formError={formError}
                loading={loading}
                user={user}
                updateUser={updateUser}
                updateLoading={updateLoading}
                password={password}
                onUpdatePassword={onUpdatePassword}
                updateError={updateError}
                updateFormError={updateFormError}
                passwordConfirm={passwordConfirm}
                onUpdatePasswordConfirm={onUpdatePasswordConfirm}
              />
            </RouteWrapper>
          }
        />
        <Route
          path="/reset-password"
          element={
            <RouteWrapper title="Reset Password">
              <ResetPassword
                error={error}
                formError={formError}
                loading={loading}
                updateUser={updateUser}
                updateLoading={updateLoading}
                user={user}
                password={password}
                onUpdatePassword={onUpdatePassword}
                updateError={updateError}
                updateFormError={updateFormError}
                passwordConfirm={passwordConfirm}
                onUpdatePasswordConfirm={onUpdatePasswordConfirm}
              />
            </RouteWrapper>
          }
        />
        <Route
          path="/mfa-setup"
          element={
            <RouteWrapper title="Two-Factor Authentication Setup">
              <MFASetup />
            </RouteWrapper>
          }
        />
        <Route
          path="/mfa-verify"
          element={
            <RouteWrapper title="Two-Factor Authentication">
              <MFAVerify />
            </RouteWrapper>
          }
        />
        <Route
          path="*"
          element={
            <div>
              Catch-all route matched. Current path: {location.pathname}
            </div>
          }
        />
      </Routes>
    </BaseLayout>
  );
}

Auth.propTypes = {
  // Define prop types if needed
  email: PropTypes.string,
  password: PropTypes.string,
  updateUser: PropTypes.func,
  updateLoading: PropTypes.func,
  updateEmail: PropTypes.func,
  onLoginSubmit: PropTypes.func,
  handleLoginKeyUp: PropTypes.func,
  forgotPassword: PropTypes.func,
  formError: PropTypes.string,
  emailSent: PropTypes.bool,
  error: PropTypes.string,
  loading: PropTypes.bool,
  user: PropTypes.shape({}),
  onUpdatePassword: PropTypes.func,
  onUpdateEmail: PropTypes.func,
  updateFormError: PropTypes.func,
  updateError: PropTypes.func,
  passwordConfirm: PropTypes.string,
  onUpdatePasswordConfirm: PropTypes.func,
  alert: PropTypes.bool,
  updateEmailSent: PropTypes.func,
};

Auth.defaultProps = {
  // Define default props if needed
  email: '',
  password: '',
  updateUser: null,
  updateLoading: null,
  updateEmail: null,
  onLoginSubmit: null,
  handleLoginKeyUp: null,
  forgotPassword: null,
  formError: null,
  emailSent: false,
  error: null,
  loading: false,
  user: null,
  onUpdatePassword: null,
  onUpdateEmail: null,
  updateFormError: null,
  updateError: null,
  passwordConfirm: '',
  onUpdatePasswordConfirm: null,
  alert: null,
  updateEmailSent: null,
};

export default Auth;
